import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Answers } from '../../search/SearchAnswers';
import { baseXUnit } from '../../styles/theme';
import RenderAsync from '../common/RenderAsync';
import {
  AnswerOption,
  Question,
  questionsActions,
  selectSearchChatQuestionCollection
} from '../../redux/features/questions';
import Modal from '../common/Modal';
import { t } from '../../utils/i18n';
import { BoxSkeleton } from '../common/skeletons';
import HorizontalList from '../common/HorizontalList';
import DisplayOn from '../common/DisplayOn';
import SearchResultsFilterButton from './SearchResultsFilterButton';
import SearchResultsFilterModalContent from './SearchResultsFilterModalContent';

interface SearchResultsFiltersProps {
  searchAnswers: Answers;
  onUpdate: (question: Question, answer: AnswerOption | null) => void;
}

const SearchResultsFilters: React.FunctionComponent<
  SearchResultsFiltersProps
> = ({ searchAnswers, onUpdate }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(questionsActions.makeSureSearchChatQuestionsLoaded());
  }, [dispatch]);
  const questionCollectionLoadable = useSelector(
    selectSearchChatQuestionCollection
  );
  const [activeQuestion, setActiveQuestion] = useState<Question | undefined>();
  const closeQuestionModal = () => setActiveQuestion(undefined);
  const openQuestionModal = (question: Question) => setActiveQuestion(question);
  const renderSearchResultsFilterButtons = (questions: Question[]) => {
    return questions.map((question) => {
      const answerOptions = searchAnswers[question.key];
      return (
        <SearchResultsFilterButton
          question={question}
          currentAnswerOption={answerOptions && answerOptions[0]}
          key={question.key}
          onClick={() => {
            openQuestionModal(question);
          }}
        />
      );
    });
  };
  const renderSkeletons = (): ReactNode => {
    return (
      <>
        <BoxSkeleton
          uniqueKey="search-results-filters-skeleton"
          height={[baseXUnit(4), baseXUnit(4), baseXUnit(12)]}
        />
      </>
    );
  };
  return (
    <div className="search-results-filters">
      <RenderAsync
        state={
          questionCollectionLoadable && questionCollectionLoadable.loadingState
        }
        onLoading={renderSkeletons}
      >
        {() => (
          <>
            <DisplayOn small medium>
              <HorizontalList>
                {renderSearchResultsFilterButtons(
                  questionCollectionLoadable.questions
                )}
              </HorizontalList>
            </DisplayOn>
            <DisplayOn large>
              {renderSearchResultsFilterButtons(
                questionCollectionLoadable.questions
              )}
            </DisplayOn>
          </>
        )}
      </RenderAsync>
      <Modal
        isOpen={!!activeQuestion}
        onDismiss={closeQuestionModal}
        ariaLabel={activeQuestion ? activeQuestion.label : t('Kysymys')}
        animateIn
      >
        {activeQuestion && (
          <SearchResultsFilterModalContent
            title={activeQuestion.label}
            close={closeQuestionModal}
            question={activeQuestion}
            onAnswer={onUpdate}
            selectedAnswerOption={getSelectedAnswerOption(
              activeQuestion,
              searchAnswers
            )}
          />
        )}
      </Modal>
    </div>
  );
};

function getSelectedAnswerOption(question: Question, searchAnswers: Answers) {
  const answerOptions = searchAnswers[question.key];
  if (!answerOptions) {
    // question not answered
    return undefined;
  }
  const firstAnswerOption = answerOptions[0];
  return question.options.find((option: AnswerOption) => {
    return option.value === firstAnswerOption;
  });
}

export default SearchResultsFilters;
